import React from "react";
import { Chip, Typography, withStyles } from "@material-ui/core";
import { useAccountCustomer } from "../../api/hooks/useAccountCustomer";
import { Customer } from "../../api/services";
import { InlineType } from "../../components/InlineType";
import { HeartIcon } from "../../icons";
import { VulnerabilityChipError } from "./components/VulnerabilityChipError";
import { useAccountGate } from "../../components/AccountGate";

const StyledChip = withStyles((theme) => ({
  root: {
    background: "#7B1FA2",
    color: theme.palette.primary.contrastText,
    paddingLeft: 4,
    paddingRight: 4,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    marginRight: theme.spacing(1),
  },
  icon: {
    color: "currentcolor",
  },
  iconSmall: {
    height: "16px",
    width: "16px",
  },
}))(Chip);

const getLabel = (customer?: Customer): string | null => {
  if (!customer) return null;

  let label = "";

  if (customer.hasPriorityServices) {
    label += "Priority service";
  }

  if (customer.hasVulnerabilities) {
    if (label.length) {
      label += " / ";
    }
    label += "Vulnerability";
  }
  return label.length ? label : null;
};

/**
 * Display the PSR and Vulnerabilities status for a specific account
 * @param accountNumber - The account the vulnerability status is displayed for
 */
export const VulnerabilitiesChip: React.FC<{ accountNumber: string }> = ({
  accountNumber,
}) => {
  const { error: accountError, isAccessible } = useAccountGate();
  const { data: customer, isError, isLoading } = useAccountCustomer(
    accountNumber,
  );

  if (isLoading || accountError || !isAccessible) {
    // Show nothing if:
    // - we are still loading the customer
    // - the account fetch itself has failed
    return null;
  }

  if (isError) {
    return (
      <VulnerabilityChipError>
        Error loading priority services / vulnerabilities
      </VulnerabilityChipError>
    );
  }

  const label = getLabel(customer);

  return label ? (
    <StyledChip
      data-testid="VulnerabilitiesChip"
      tabIndex={-1}
      size="small"
      icon={<HeartIcon fill="currentColor" />}
      label={
        <Typography variant="caption">
          <InlineType fontWeight="bold">{label}</InlineType>
        </Typography>
      }
    />
  ) : null;
};
